@import "../../global/temporary-mercury-overrides/_button.scss";

// The component reproduces the Mercury "Set required properties" connection
// dialog. Real Mercury classes (.dialog-header-with-border, .field-group-
// justified-end, .label, .input, .combo-box, .button-*, typography) come from
// the loaded bundles; the rules below only add the scaffolding Mercury does
// not define (hero banner, badge, close button, field help) using DS tokens.

:host {
  display: grid;
  block-size: 100%;
  overflow: hidden;
  grid-template-rows: max-content 1fr max-content;
  background-color: var(--color-accent-surface-elevation-1);
  color: var(--color-text-neutral-default);
}

.section {
  // section is just for semantics; the host owns the grid.
  display: contents;
}

// #region Hero
.hero {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-gap-xl);
  padding-block: var(--spacing-padding-l);
  padding-inline: var(--spacing-padding-l);
  border-block-end: var(--size-1) solid
    var(--color-border-surface-on-elevation-1);
}

.hero__text {
  flex: 1;
  min-inline-size: 0;
}

.hero__title {
  margin: 0;
  color: var(--color-text-neutral-default);
}

.hero__description {
  margin-block: var(--spacing-gap-xs) 0;
  margin-inline: 0;
  color: var(--color-text-neutral-disabled);
  text-wrap: pretty;
}

.hero__badge {
  flex: none;
  display: grid;
  place-items: center;
  inline-size: 44px;
  block-size: 44px;
  border-radius: var(--border-radius-m);
  background-color: var(--color-accent-item-hover);
  color: var(--color-icon-primary-default);
}
// #endregion Hero

// #region Body
.main {
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-xxl);
  padding-block: var(--spacing-padding-l);
  padding-inline: var(--spacing-padding-l);
}

// Field section: a quiet uppercase header + its field grid. Sections after
// the first get a divider so the body reads as structured groups.
.field-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-l);
}

.field-section + .field-section {
  padding-block-start: var(--spacing-padding-xl);
  border-block-start: var(--size-1) solid
    var(--color-border-surface-on-elevation-1);
}

.field-section__head {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-gap-m);
}

.field-section__title {
  font-family: var(--font-family-body);
  font-weight: var(--font-style-semi-bold);
  font-size: var(--font-size-caption-m);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-neutral-neutral);
}

// Two-column field grid. Each field occupies one column; checkbox fields
// (and any other field marked as full-width) span the entire row, forcing
// the following fields to wrap onto the next row.
.fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-gap-l);
}

.field--full {
  grid-column: 1 / -1;
}

// Inline checkbox field: control + label on the same row, optional help text
// flows below aligned with the label.
.field--checkbox {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-gap-xs);
}

.field--checkbox .field__row {
  display: flex;
  align-items: center;
  gap: var(--spacing-gap-xs);
}

.field--checkbox .label {
  margin: 0;
  cursor: pointer;
}

// Help text below a checkbox, indented to align with the label past the box.
.field--checkbox .field__hint {
  margin: 0;
  margin-inline-start: calc(var(--spacing-gap-m) + var(--spacing-gap-m));
  color: var(--color-text-neutral-disabled);
  text-wrap: pretty;
}
// #endregion Body

// #region Footer
.footer {
  padding-block: var(--spacing-padding-m);
  padding-inline: var(--spacing-padding-l);
}
// #endregion Footer
